import type { FC } from 'react';
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { createUseStyles } from 'react-jss';
import cn from 'classnames';

import MediaPlayer from 'app/lib/MediaPlayer';
import { useAppContext } from 'app/lib/AppContext';
import useResizeObserver from 'app/hooks/useResizeObserver';
import usePixel from 'app/hooks/usePixel';
import { useSelector } from 'app/store/utils/redux/hooks';

const GetApp: FC = (props) => {
  const classes = useStyles();
  const { bowser, mode } = useAppContext();

  const isIos = bowser.os.name?.toLowerCase() === 'ios';
  const isSafari = bowser.browser.name?.toLowerCase() === 'safari';
  const isAndroid = bowser.os.name?.toLowerCase() === 'android';
  const isLoaded = useSelector((state) => state?.card?.meta?.status === 'ready');
  const [isVisibleLogged, setVisibleLogged] = useState<boolean>(false);
  const [isReady, setReady] = useState<boolean>(false);
  const [isVisible, setVisible] = useState<boolean>(false);
  const [height, setHeight] = useState<number>(0);

  const pixel = usePixel();

  useResizeObserver(useCallback((event: any) => {
    const { contentRect } = event;
    if (contentRect.height > 700) {
      setHeight(56);
    } else
    if (contentRect.height < 640) {
      setHeight(46);
    } else {
      const percent = (contentRect.height - 640) / 60;
      setHeight(46 + 10 * percent);
    }
    if (contentRect.height < 620 || contentRect.width > 450) {
      setVisible(false);
    } else {
      setVisible(true);
    }
  }, []), '#root');

  useEffect(() => {
    if (!isLoaded) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setReady(true);
    }, 2000);
    return () => {
      clearTimeout(timer);
    };
  }, [isLoaded]);

  useEffect(() => {
    if (isVisibleLogged) {
      return;
    }
    if (!(isLoaded && isReady && isVisible)) {
      return;
    }
    setVisibleLogged(true);
    pixel.enable('https://view.adjust.com/impression/1xr84dy');
  }, [pixel, isVisibleLogged, isLoaded, isReady, isVisible]);

  const handlePress = useCallback(() => {
    if (isIos && isSafari) {
      MediaPlayer.muteAll('deep-link');
      setTimeout(() => {
        MediaPlayer.unmuteAll('deep-link');
      }, 3000);
    }
    window.open('https://cardsnacks.go.link/lBq3x', '_blank');
  }, [isIos, isSafari]);

  const renderButton = useMemo(() => {
    if (isIos) {
      return <button className={classes.button} onClick={handlePress} type="button">Get the free app</button>;
    }
    if (isAndroid) {
      return <a className={classes.button} href="https://cardsnacks.go.link/lBq3x" target="_top">Get the free app</a>;
    }
    return <a className={classes.button} href="https://cardsnacks.go.link/lBq3x" target="_blank" rel="noreferrer">Get the free app</a>;
  }, [isIos, isAndroid, handlePress]);

  return (
    <div className={cn(classes.GetApp, { [classes.isVisible]: isLoaded && isReady && isVisible && mode !== 'preview' })} style={{ height }}>
      <div className={classes.text}>card made with CardSnacks</div>
      {renderButton}
    </div>
  );
};

const useStyles = createUseStyles({
  GetApp: {
    display: 'flex',
    position: 'fixed',
    right: 0,
    bottom: 0,
    left: 0,
    background: '#eeeae4',
    zIndex: 1000,
    justifyContent: 'center',
    opacity: 0,
    pointerEvents: 'none',
    transition: 'opacity 400ms ease',
  },
  isVisible: {
    opacity: 1,
    pointerEvents: 'all',
  },
  text: {
    fontSize: '1.3em',
    fontWeight: 300,
    alignSelf: 'center',
    color: '#3f3f3f',
  },
  button: {
    display: 'flex',
    fontSize: '1.3em',
    fontWeight: 500,
    padding: '0 calc(1.2em / 1.3)',
    background: '#f9f7f4',
    color: '#3f3f3f',
    height: 'calc(3.2em / 1.3)',
    borderRadius: 'calc(1.6em / 1.3)',
    alignSelf: 'center',
    alignItems: 'center',
    marginLeft: 'calc(2.4em / 1.3)',
    border: 0,
  },
});

export default GetApp;
